import * as _ from 'lodash';
import { AgGridReact } from 'ag-grid-react';
import { GridWrapper } from './styles';
import { BodyScrollEvent, CellClickedEvent, ColDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { FormEvent, useMemo, useState } from 'react';
import { IExpertSystemOrderSummaryWithComments, Section } from './models';
import { Button, FormControl, Glyphicon } from 'react-bootstrap';
import SortableHeaderComponent from '../admin/center/list/common/sortable-header';
import ActionButtonRenderer from './action-button-renderer';
import { Utility } from '@nkr/services/utility';

interface IProps {
    list: IExpertSystemOrderSummaryWithComments[],
    statusId: Section,
    centerId: number,
    onShowDonors: (expertSystemId: number) => void,
    onChangeComments: (expertSystemId: number, comments: string) => void,
    onSaveComments: (expertSystemId: number) => void,
    onDiscardCommentChanges: (expertSystemId: number) => void,
    onOpenOrder: (expertSystemId: number) => void,
    onPrintRequisition: (orderId: number, entityTypeId: number) => void,
    onWithDrawDonor: (orderId: number) => void
}

const Grid = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const onGridReady = (e: GridReadyEvent) => {

    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onSortChanged = (e: SortChangedEvent) => {

    };

    const onBodyScroll = (e: BodyScrollEvent) => {
        // e.api.sizeColumnsToFit();
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {

    };

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const handleChangeComments = (expertSystemId: number, comments: string) => {
        props.onChangeComments(expertSystemId, comments);
    };

    const handleSaveComments = (expertSystemId: number) => {
        props.onSaveComments(expertSystemId);
    };

    const handleDiscardCommentChanges = (expertSystemId: number) => {
        props.onDiscardCommentChanges(expertSystemId);
    };

    const columns: ColDef[] = [
        {
            colId: 'expertSystemId',
            field: 'expertSystemId',
            headerName: 'Order #',
            width: 65,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Order #</SortableHeaderComponent>,
        },
        {
            colId: 'donorAlias',
            field: 'donorAlias',
            headerName: 'Donor Alias',
            width: 95,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor<br />Alias</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IExpertSystemOrderSummaryWithComments;

                return (
                    <a target='_blank' href={`center/${props.centerId}/donor/${data.donorId}/profile/donor-data`}>{data.donorAlias}</a>
                );
            }
        },
        {
            colId: 'donorName',
            field: 'donorName',
            headerName: 'Donor Name',
            width: 145,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor<br />Name</SortableHeaderComponent>,
        },
        {
            colId: 'donorBirthDate',
            field: 'donorBirthDate',
            headerName: 'Donor DOB',
            width: 105,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor<br />DOB</SortableHeaderComponent>,
            comparator: Utility.dateComparator
        },
        {
            colId: 'recipientAlias',
            field: 'recipientAlias',
            headerName: 'Recipient Alias',
            width: 105,
            hide: (props.statusId != Section.PendingDonor) == false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient<br />Alias</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IExpertSystemOrderSummaryWithComments;

                return (
                    <a onClick={() => props.onShowDonors(data.expertSystemId)}>{data.recipientAlias}</a>
                );
            }
        },
        {
            colId: 'recipientName',
            field: 'recipientName',
            headerName: 'Recipient Name',
            width: 145,
            hide: (props.statusId != Section.PendingDonor) == false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient<br />Name</SortableHeaderComponent>,
        },
        {
            colId: 'recipientBirthDate',
            field: 'recipientBirthDate',
            headerName: 'Recipient DOB',
            width: 105,
            hide: (props.statusId != Section.PendingDonor) == false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient<br />DOB</SortableHeaderComponent>,
            comparator: Utility.dateComparator
        },
        {
            colId: 'status',
            field: 'status',
            headerName: 'Status',
            width: 165,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>,
        },
        {
            colId: 'donorOrderedKitType',
            field: 'donorOrderedKitType',
            headerName: 'Donor Ordered Kit Type',
            width: 165,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor<br />Ordered Kit Type</SortableHeaderComponent>,
        },
        {
            colId: 'recipientOrderedKitType',
            field: 'recipientOrderedKitType',
            headerName: 'Recipient Ordered Kit Type',
            width: 165,
            hide: (props.statusId != Section.PendingDonor && props.statusId != Section.PendingCenter) == false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient<br />Ordered Kit Type</SortableHeaderComponent>,
        },
        {
            colId: 'donorTyping',
            field: 'donorTyping',
            headerName: 'Donor Typing Completed',
            width: 85,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor<br />Typing<br />Completed</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IExpertSystemOrderSummaryWithComments;
                return <>{data.donorTyping ? 'Yes' : 'No'}</>
            },
            valueGetter: (params: ValueGetterParams) => params.data.donorTyping ? 'Yes' : 'No'
        },
        {
            colId: 'recipientTyping',
            field: 'recipientTyping',
            headerName: 'Recipient Typing Completed',
            width: 85,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient<br />Typing<br />Completed</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IExpertSystemOrderSummaryWithComments;
                return <>{data.recipientTyping ? 'Yes' : 'No'}</>
            },
            valueGetter: (params: ValueGetterParams) => params.data.recipientTyping ? 'Yes' : 'No'
        },
        {
            colId: 'inSwap',
            field: 'statusId',
            headerName: 'In Swap',
            width: 115,
            hide: (props.statusId == Section.Completed) == false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>In Swap</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IExpertSystemOrderSummaryWithComments;
                return <>{data.isInSwap ? 'Yes' : 'No'}</>
            },
            valueGetter: (params: ValueGetterParams) => params.data.isInSwap ? 'Yes' : 'No'
        },
        {
            colId: 'comments',
            headerName: 'Comments',
            width: 215,
            sortable: false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Comments</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IExpertSystemOrderSummaryWithComments;

                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <FormControl
                            bsSize='xs'
                            value={data.newComments}
                            onChange={(e: FormEvent<FormControl & HTMLInputElement>) => handleChangeComments(params.data.expertSystemId, e.currentTarget.value)}
                        />
                        <div>
                            <Button bsSize='xs' disabled={data.isChanged == false} title='Save comments' onClick={() => handleSaveComments(data.expertSystemId)}><Glyphicon glyph='ok' style={{ color: params.data.isChanged ? 'blue' : 'lightgray' }} /></Button>
                            <Button bsSize='xs' disabled={data.isChanged == false} title='Discard changes' onClick={() => handleDiscardCommentChanges(data.expertSystemId)}><Glyphicon glyph='remove' style={{ color: params.data.isChanged ? 'red' : 'lightgray' }} /></Button>
                        </div>
                    </div>
                );
            }
        },
        {
            colId: 'actions',
            headerName: 'Actions',
            width: 115,
            sortable: false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Actions</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                return (
                    <ActionButtonRenderer
                        data={params.data}
                        onMenuToggle={handleMenuToggle}
                        onOpenExpertAnalysis={props.onOpenOrder}
                        onPrintRequisition={props.onPrintRequisition}
                        onWithDrawDonor={props.onWithDrawDonor} />
                )
            }
        },
    ];

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.list, [props.list]);

    return (
        <GridWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                defaultColDef={{
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    sortable: true
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={54}
                rowHeight={32}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                enableCellTextSelection={true}
                suppressContextMenu={true}
                immutableData={true}
                getRowNodeId={(data: IExpertSystemOrderSummaryWithComments) => data.expertSystemId.toString()}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onSortChanged={onSortChanged}
                onBodyScroll={onBodyScroll}
                onGridSizeChanged={onGridSizeChanged}
            />
        </GridWrapper>
    );
}

export default Grid;